import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LaboratoryData } from "../types/MedicalData";

// Type for sign-off data
export interface ReportSignOffData {
  id?: string;
  reportId: string;
  month: string;
  signedBy: string;
  signedByName: string;
  signedAt: string;
  comments?: string;
}

// Type for report data that has rows
interface ReportDataWithRows {
  reportDate: {
    startDate: string;
    endDate: string;
  };
  rows: any[];
  totalCases?: number;
  signOff?: ReportSignOffData;
}

// Type for report data that has sections (like CytotechDiagnosisSummaryData)
type ReportDataWithSections = {
  reportDate: {
    startDate: string;
    endDate: string;
  };
  sections?: any[];
  overallSummary?: any[];
  overallTotal?: {
    objectid: number;
    objectid2: number;
  };
  totalCases?: number;
  // For GynAdequacySummaryData
  satisfactory?: { comment: string; count: number }[];
  unsatisfactory?: { comment: string; count: number }[];
  totalSatisfactory?: number;
  totalUnsatisfactory?: number;
  // For GynAdequacyBySourceData
  sources?: Array<{
    procSite: string;
    rows: any[];
    totalCases: number;
  }>;
  totalSources?: number;
  // For HpvTotalThinprepData
  totalSpecimens?: number;
};

// Extend jsPDF type to include autoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

/**
 * Helper function to format date values
 * Converts ISO date strings, JSON date strings, or Date objects to readable format
 */
const formatDateValue = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // If it's already a formatted date string (doesn't contain T or Z), return as-is
  if (
    typeof value === "string" &&
    !value.includes("T") &&
    !value.includes("Z")
  ) {
    // Check if it's already in a readable format (YYYY-MM-DD or MM/DD/YYYY)
    if (
      /^\d{4}-\d{2}-\d{2}$/.test(value) ||
      /^\d{2}\/\d{2}\/\d{4}$/.test(value)
    ) {
      return value;
    }
  }

  try {
    const date = new Date(value);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(value);
    }
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (e) {
    // If parsing fails, return as string
    return String(value);
  }
};

/**
 * Helper function to check if a field name is likely a date field
 */
const isDateField = (fieldName: string): boolean => {
  const dateFieldPatterns = [
    /date/i,
    /signout/i,
    /sign.*out/i,
    /timestamp/i,
    /created/i,
    /updated/i,
  ];
  return dateFieldPatterns.some((pattern) => pattern.test(fieldName));
};

/**
 * Helper function to format a value, applying date formatting if it's a date field
 */
const formatValue = (value: any, fieldName: string): any => {
  if (isDateField(fieldName)) {
    return formatDateValue(value);
  }
  return value;
};

/**
 * Export laboratory data to CSV format
 */
export const exportToCSV = (
  data: LaboratoryData[],
  dateRange?: { startDate: string; endDate: string }
): Blob => {
  // Create CSV header
  const headers = [
    "ID",
    "Title",
    "Category",
    "Current Value",
    "Previous Value",
    "Trend",
    "Trend Percentage",
    "Unit",
    "Priority",
    "CLIA Category",
    "CLIA Requirement",
    "Regulatory Standard",
    "Description",
  ];

  // Create CSV rows
  const rows = data.map((item) => [
    item.id,
    item.title,
    item.category,
    item.currentValue.toString(),
    item.previousValue.toString(),
    item.trend,
    `${item.trendPercentage}%`,
    item.unit,
    item.priority,
    item.cliaCategory.type,
    item.cliaCategory.cliaRequirement,
    item.cliaCategory.regulatoryStandard,
    item.description.replace(/,/g, ";"), // Replace commas in description to avoid CSV issues
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  // Add date range info if provided
  let finalContent = csvContent;
  if (dateRange) {
    const dateInfo = `\n\nDate Range: ${dateRange.startDate} to ${dateRange.endDate}`;
    finalContent = csvContent + dateInfo;
  }

  return new Blob([finalContent], { type: "text/csv;charset=utf-8;" });
};

/**
 * Export laboratory data to Excel format
 */
export const exportToExcel = (
  data: LaboratoryData[],
  dateRange?: { startDate: string; endDate: string }
): Blob => {
  // Prepare data for Excel
  const excelData = data.map((item) => ({
    ID: item.id,
    Title: item.title,
    Category: item.category,
    "Current Value": item.currentValue,
    "Previous Value": item.previousValue,
    Trend: item.trend,
    "Trend Percentage": `${item.trendPercentage}%`,
    Unit: item.unit,
    Priority: item.priority,
    "CLIA Category": item.cliaCategory.type,
    "CLIA Requirement": item.cliaCategory.cliaRequirement,
    "Regulatory Standard": item.cliaCategory.regulatoryStandard,
    Description: item.description,
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Set column widths
  const columnWidths = [
    { wch: 20 }, // ID
    { wch: 30 }, // Title
    { wch: 20 }, // Category
    { wch: 15 }, // Current Value
    { wch: 15 }, // Previous Value
    { wch: 10 }, // Trend
    { wch: 15 }, // Trend Percentage
    { wch: 10 }, // Unit
    { wch: 12 }, // Priority
    { wch: 25 }, // CLIA Category
    { wch: 30 }, // CLIA Requirement
    { wch: 30 }, // Regulatory Standard
    { wch: 50 }, // Description
  ];
  worksheet["!cols"] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Laboratory Metrics");

  // Add metadata sheet if date range is provided
  if (dateRange) {
    const metadata = [
      ["Export Date", new Date().toISOString().split("T")[0]],
      ["Date Range Start", dateRange.startDate],
      ["Date Range End", dateRange.endDate],
      ["Total Metrics", data.length],
    ];
    const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, "Metadata");
  }

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });

  return new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

/**
 * Export laboratory data to PDF format
 */
export const exportToPDF = (
  data: LaboratoryData[],
  dateRange?: { startDate: string; endDate: string }
): Blob => {
  const doc = new jsPDF("landscape", "mm", "a4");

  // Add title
  doc.setFontSize(18);
  doc.text("CLIA Laboratory Analytics - Export Report", 14, 15);

  // Add date range if provided
  doc.setFontSize(10);
  if (dateRange) {
    doc.text(
      `Date Range: ${dateRange.startDate} to ${dateRange.endDate}`,
      14,
      22
    );
  }
  doc.text(`Export Date: ${new Date().toLocaleDateString()}`, 14, 27);
  doc.text(`Total Metrics: ${data.length}`, 14, 32);

  // Prepare table data
  const tableData = data.map((item) => [
    item.id,
    item.title,
    item.category,
    item.currentValue.toString(),
    item.previousValue.toString(),
    item.trend,
    `${item.trendPercentage}%`,
    item.unit,
    item.priority,
  ]);

  // Add table
  autoTable(doc, {
    head: [
      [
        "ID",
        "Title",
        "Category",
        "Current",
        "Previous",
        "Trend",
        "Trend %",
        "Unit",
        "Priority",
      ],
    ],
    body: tableData,
    startY: dateRange ? 38 : 33,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10, right: 14, bottom: 10, left: 14 },
    columnStyles: {
      0: { cellWidth: 25 }, // ID
      1: { cellWidth: 40 }, // Title
      2: { cellWidth: 30 }, // Category
      3: { cellWidth: 20 }, // Current
      4: { cellWidth: 20 }, // Previous
      5: { cellWidth: 15 }, // Trend
      6: { cellWidth: 20 }, // Trend %
      7: { cellWidth: 15 }, // Unit
      8: { cellWidth: 20 }, // Priority
    },
  });

  // Add footer with page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  // Generate PDF blob
  return doc.output("blob");
};

/**
 * Export report data with rows to CSV format
 */
export const exportReportDataToCSV = (
  reportData: ReportDataWithRows,
  reportTitle: string
): Blob => {
  if (!reportData.rows || reportData.rows.length === 0) {
    throw new Error("No data to export");
  }

  // Get headers from first row
  const firstRow = reportData.rows[0];
  const headers = Object.keys(firstRow);

  // Create CSV rows with date formatting
  const rows = reportData.rows.map((row) =>
    headers.map((header) => {
      const value = formatValue(row[header], header);
      return value !== null && value !== undefined ? String(value) : "";
    })
  );

  // Combine headers and rows
  const csvContent = [
    headers.map((h) => `"${h}"`).join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
    ),
  ].join("\n");

  // Add metadata
  const metadataLines = [
    `Report: ${reportTitle}`,
    `Date Range: ${reportData.reportDate.startDate} to ${reportData.reportDate.endDate}`,
    `Total Cases: ${reportData.totalCases || reportData.rows.length}`,
    `Export Date: ${new Date().toISOString().split("T")[0]}`,
  ];

  // Add sign-off information if available
  if (reportData.signOff) {
    metadataLines.push("");
    metadataLines.push("Report Sign-Off:");
    metadataLines.push(`  Signed by: ${reportData.signOff.signedByName}`);
    metadataLines.push(
      `  Signed on: ${new Date(reportData.signOff.signedAt).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      )}`
    );
    if (reportData.signOff.comments) {
      metadataLines.push(`  Comments: ${reportData.signOff.comments}`);
    }
  }

  const metadata = metadataLines.join("\n");

  const finalContent = metadata + "\n\n" + csvContent;

  return new Blob([finalContent], { type: "text/csv;charset=utf-8;" });
};

/**
 * Export report data with rows to Excel format
 */
export const exportReportDataToExcel = (
  reportData: ReportDataWithRows,
  reportTitle: string
): Blob => {
  if (!reportData.rows || reportData.rows.length === 0) {
    throw new Error("No data to export");
  }

  // Get headers from first row
  const firstRow = reportData.rows[0];
  const headers = Object.keys(firstRow);

  // Prepare data for Excel with date formatting
  const excelData = reportData.rows.map((row) => {
    const obj: any = {};
    headers.forEach((header) => {
      const value = formatValue(row[header], header);
      obj[header] = value !== null && value !== undefined ? value : "";
    });
    return obj;
  });

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Set column widths (auto-size based on content)
  const columnWidths = headers.map(() => ({ wch: 20 }));
  worksheet["!cols"] = columnWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Report Data");

  // Add metadata sheet
  const metadata: any[][] = [
    ["Report", reportTitle],
    ["Date Range Start", reportData.reportDate.startDate],
    ["Date Range End", reportData.reportDate.endDate],
    ["Total Cases", reportData.totalCases || reportData.rows.length],
    ["Export Date", new Date().toISOString().split("T")[0]],
  ];

  // Add sign-off information if available
  if (reportData.signOff) {
    metadata.push([]);
    metadata.push(["Report Sign-Off", ""]);
    metadata.push(["Signed by", reportData.signOff.signedByName]);
    metadata.push([
      "Signed on",
      new Date(reportData.signOff.signedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    ]);
    if (reportData.signOff.comments) {
      metadata.push(["Comments", reportData.signOff.comments]);
    }
  }
  const metadataSheet = XLSX.utils.aoa_to_sheet(metadata);
  XLSX.utils.book_append_sheet(workbook, metadataSheet, "Metadata");

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });

  return new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

/**
 * Export report data with rows to PDF format
 */
export const exportReportDataToPDF = (
  reportData: ReportDataWithRows,
  reportTitle: string
): Blob => {
  if (!reportData.rows || reportData.rows.length === 0) {
    throw new Error("No data to export");
  }

  const doc = new jsPDF("landscape", "mm", "a4");

  // Get headers from first row
  const firstRow = reportData.rows[0];
  const headers = Object.keys(firstRow);

  // Add title
  doc.setFontSize(18);
  doc.text(reportTitle, 14, 15);

  // Add metadata
  doc.setFontSize(10);
  let yPos = 22;
  doc.text(
    `Date Range: ${reportData.reportDate.startDate} to ${reportData.reportDate.endDate}`,
    14,
    yPos
  );
  yPos += 5;
  doc.text(
    `Total Cases: ${reportData.totalCases || reportData.rows.length}`,
    14,
    yPos
  );
  yPos += 5;
  doc.text(`Export Date: ${new Date().toLocaleDateString()}`, 14, yPos);

  // Add sign-off information if available
  if (reportData.signOff) {
    yPos += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Report Sign-Off:", 14, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Signed by: ${reportData.signOff.signedByName}`, 14, yPos);
    yPos += 5;
    doc.text(
      `Signed on: ${new Date(reportData.signOff.signedAt).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      )}`,
      14,
      yPos
    );
    if (reportData.signOff.comments) {
      yPos += 5;
      doc.text(`Comments: ${reportData.signOff.comments}`, 14, yPos);
    }
    yPos += 5;
  }

  // Prepare table data with date formatting
  const tableData = reportData.rows.map((row) =>
    headers.map((header) => {
      const value = formatValue(row[header], header);
      return value !== null && value !== undefined ? String(value) : "";
    })
  );

  // Calculate column widths (distribute evenly)
  const pageWidth = doc.internal.pageSize.getWidth() - 28; // margins
  const colWidth = pageWidth / headers.length;

  // Calculate table start position based on whether sign-off was added
  const tableStartY = reportData.signOff ? yPos + 5 : 35;

  // Add table
  autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: tableStartY,
    styles: { fontSize: 7 },
    headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: 10, right: 14, bottom: 10, left: 14 },
    columnStyles: headers.reduce((acc, _, index) => {
      acc[index] = { cellWidth: colWidth };
      return acc;
    }, {} as any),
  });

  // Add footer with page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  // Generate PDF blob
  return doc.output("blob");
};

/**
 * Main export function that routes to the appropriate format
 */
export const exportData = (
  format: "csv" | "excel" | "pdf",
  data: LaboratoryData[],
  dateRange?: { startDate: string; endDate: string }
): Blob => {
  switch (format) {
    case "csv":
      return exportToCSV(data, dateRange);
    case "excel":
      return exportToExcel(data, dateRange);
    case "pdf":
      return exportToPDF(data, dateRange);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

/**
 * Convert sections-based report data to rows format for export
 */
const convertSectionsToRows = (reportData: ReportDataWithSections): any[] => {
  const rows: any[] = [];

  // Handle HpvTotalThinprepData structure (simple totals)
  if (
    reportData.totalCases !== undefined &&
    reportData.totalSpecimens !== undefined &&
    !reportData.sources &&
    !reportData.satisfactory
  ) {
    rows.push({
      Metric: "Total Cases",
      Value: reportData.totalCases || 0,
    });
    rows.push({
      Metric: "Total Specimens",
      Value: reportData.totalSpecimens || 0,
    });
    return rows;
  }

  // Handle GynAdequacyBySourceData structure (sources with rows)
  if (reportData.sources && reportData.sources.length > 0) {
    reportData.sources.forEach((source) => {
      const procSite = source.procSite || "";
      if (source.rows && source.rows.length > 0) {
        source.rows.forEach((row: any) => {
          rows.push({
            "Procedure Site": procSite,
            Adequacy: row.adequacy || "",
            Description: row.description || "",
            "Total Cases": row.totalCases || 0,
            Percentage: row.percentage ? `${row.percentage.toFixed(2)}%` : "0%",
          });
        });
        // Add source total
        rows.push({
          "Procedure Site": procSite,
          Adequacy: "Total",
          Description: "",
          "Total Cases": source.totalCases || 0,
          Percentage: "100%",
        });
      }
    });
    return rows;
  }

  // Handle GynAdequacySummaryData structure (satisfactory/unsatisfactory)
  if (reportData.satisfactory || reportData.unsatisfactory) {
    // Add satisfactory rows
    if (reportData.satisfactory && reportData.satisfactory.length > 0) {
      reportData.satisfactory.forEach((item) => {
        rows.push({
          Status: "Satisfactory",
          Comment: item.comment || "",
          Count: item.count || 0,
        });
      });
      if (reportData.totalSatisfactory !== undefined) {
        rows.push({
          Status: "Satisfactory",
          Comment: "Total",
          Count: reportData.totalSatisfactory,
        });
      }
    }

    // Add unsatisfactory rows
    if (reportData.unsatisfactory && reportData.unsatisfactory.length > 0) {
      reportData.unsatisfactory.forEach((item) => {
        rows.push({
          Status: "Unsatisfactory",
          Comment: item.comment || "",
          Count: item.count || 0,
        });
      });
      if (reportData.totalUnsatisfactory !== undefined) {
        rows.push({
          Status: "Unsatisfactory",
          Comment: "Total",
          Count: reportData.totalUnsatisfactory,
        });
      }
    }

    return rows;
  }

  // Add overall summary if available
  if (reportData.overallSummary && reportData.overallSummary.length > 0) {
    reportData.overallSummary.forEach((item) => {
      rows.push({
        Section: "Overall Summary",
        Category: item.category || "",
        Count: item.objectid || 0,
        Percentage: item.objectid2 ? `${item.objectid2.toFixed(2)}%` : "0%",
      });
    });

    // Add overall total if available
    if (reportData.overallTotal) {
      rows.push({
        Section: "Overall Summary",
        Category: "Total",
        Count: reportData.overallTotal.objectid || 0,
        Percentage: reportData.overallTotal.objectid2
          ? `${reportData.overallTotal.objectid2.toFixed(2)}%`
          : "0%",
      });
    }
  }

  // Add sections data
  if (reportData.sections && reportData.sections.length > 0) {
    reportData.sections.forEach((section) => {
      const sectionName = section.sectionName || "";

      // Add categories in section
      if (section.categories && section.categories.length > 0) {
        section.categories.forEach((item: any) => {
          rows.push({
            Section: sectionName,
            Category: item.category || "",
            Count: item.objectid || 0,
            Percentage: item.objectid2 ? `${item.objectid2.toFixed(2)}%` : "0%",
          });
        });
      }

      // Add section total if available
      if (section.total) {
        rows.push({
          Section: sectionName,
          Category: "Total",
          Count: section.total.objectid || 0,
          Percentage: section.total.objectid2
            ? `${section.total.objectid2.toFixed(2)}%`
            : "0%",
        });
      }
    });
  }

  return rows;
};

/**
 * Export report data (for report pages) - handles both rows and sections
 */
export const exportReportData = (
  format: "csv" | "excel" | "pdf",
  reportData: ReportDataWithRows | ReportDataWithSections,
  reportTitle: string,
  signOff?: ReportSignOffData
): Blob => {
  // Check if data has rows or sections
  const hasRows =
    "rows" in reportData && reportData.rows && reportData.rows.length > 0;
  const hasSections =
    "sections" in reportData || "overallSummary" in reportData;
  const hasAdequacyData =
    "satisfactory" in reportData || "unsatisfactory" in reportData;
  const hasSources = "sources" in reportData;

  let dataToExport: ReportDataWithRows;

  if (hasRows) {
    dataToExport = reportData as ReportDataWithRows;
  } else if (hasSections || hasAdequacyData || hasSources) {
    // Convert sections or adequacy data to rows format
    const rows = convertSectionsToRows(reportData as ReportDataWithSections);
    if (rows.length === 0) {
      throw new Error("No data to export");
    }
    dataToExport = {
      reportDate: reportData.reportDate,
      rows,
      totalCases: reportData.totalCases || rows.length,
    };
  } else {
    throw new Error("No exportable data found in report");
  }

  // Add sign-off information if provided
  if (signOff) {
    dataToExport.signOff = signOff;
  }

  switch (format) {
    case "csv":
      return exportReportDataToCSV(dataToExport, reportTitle);
    case "excel":
      return exportReportDataToExcel(dataToExport, reportTitle);
    case "pdf":
      return exportReportDataToPDF(dataToExport, reportTitle);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};
